import type { EntityStatusTone } from '../../../components/entity-detail/EntityStatusChip.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
// Run Center: automation journal with Cancel/Retry (Stage 3B).
//
// Shows recent ScenarioRuns of the current workspace with human-readable
// scenario names, statuses, deal/contact links and safe outcome buckets.
// The list polls on an interval so worker status transitions appear without
// a page reload. Cancel (awaiting_approval -> cancelled) and Retry
// (failed -> queued) are scoped by workspaceId and guarded by status.
import { useMemo, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@govard/i18n-core/react';
import {
  Button,
  Card,
  Col,
  Modal,
  Popconfirm,
  Result,
  Row,
  Space,
  Statistic,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';

import { formatDateTime } from '../../../shared/format/index.js';
import { EntityLink } from '../../../shared/ui/EntityLink.js';
import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { FilterBar, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { LIST_ICON_BUTTON_STYLE, LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietListSkeleton } from '../../../shared/ui/quiet-states.js';

import {
  RUN_CENTER_OVERVIEW_LIMIT,
  type RunCenterRun,
  type RunOutcome,
  type RunStatus,
} from '../api/run-center.client.js';
import type { Breakpoint } from 'antd';
import type { RunColumnKey } from '../lib/runCenterColumns.js';
import { runStartedDisplay, RUN_CENTER_COLUMN_META } from '../lib/runCenterColumns.js';
import { buildScenarioFilterOptions } from '../lib/runCenterScenarios.js';
import { useRunCenter } from '../hooks/useRunCenter.js';
import { useRunCenterMutations } from '../hooks/useRunCenterMutations.js';
import { useBillingAccess } from '../../../shared/scope/useCurrentScope.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';

const { Text } = Typography;

type StatusFilter = RunStatus | 'all';

const STATUS_TAG_TONE: Record<RunStatus, EntityStatusTone> = {
  queued: 'default',
  running: 'processing',
  awaiting_approval: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const OUTCOME_KEYS: Record<RunOutcome, string> = {
  queued: 'crm.run_center.outcome.queued',
  in_progress: 'crm.run_center.outcome.in_progress',
  waiting_approval: 'crm.run_center.outcome.waiting_approval',
  follow_up_queued: 'crm.run_center.outcome.follow_up_queued',
  skipped: 'crm.run_center.outcome.skipped',
  completed: 'crm.run_center.outcome.completed',
  failed: 'crm.run_center.outcome.failed',
  cancelled: 'crm.run_center.outcome.cancelled',
  unknown: 'crm.run_center.outcome.unknown',
};

const STATUS_KEYS: Record<RunStatus, string> = {
  queued: 'crm.run_center.status.queued',
  running: 'crm.run_center.status.running',
  awaiting_approval: 'crm.run_center.status.awaiting_approval',
  completed: 'crm.run_center.status.completed',
  failed: 'crm.run_center.status.failed',
  cancelled: 'crm.run_center.status.cancelled',
};

const ALL_STATUSES: readonly RunStatus[] = [
  'queued',
  'running',
  'awaiting_approval',
  'completed',
  'failed',
  'cancelled',
];

function useRunCenterLabels() {
  const { t } = useTranslation();
  return {
    title: t('crm.run_center.title'),
    subtitle: t('crm.run_center.subtitle'),
    stats: {
      total: t('crm.run_center.stats.total'),
      running: t('crm.run_center.stats.running'),
      awaiting: t('crm.run_center.stats.awaiting'),
      completed: t('crm.run_center.stats.completed'),
      failed: t('crm.run_center.stats.failed'),
    },
    scenarioFilter: t('crm.run_center.filter.scenario'),
    statusFilter: t('crm.run_center.filter.status'),
    allScenarios: t('crm.run_center.filter.all_scenarios'),
    allStatuses: t('crm.run_center.filter.all_statuses'),
    refresh: t('crm.run_center.refresh'),
    columns: {
      scenario: t('crm.run_center.column.scenario'),
      deal: t('crm.run_center.column.deal'),
      contact: t('crm.run_center.column.contact'),
      status: t('crm.run_center.column.status'),
      started: t('crm.run_center.column.started'),
      finished: t('crm.run_center.column.finished'),
      scheduled: t('crm.run_center.column.scheduled'),
      attempts: t('crm.run_center.column.attempts'),
      outcome: t('crm.run_center.column.outcome'),
      approval: t('crm.run_center.column.approval'),
      actions: t('crm.run_center.column.actions'),
    },
    action: {
      cancel: t('crm.run_center.action.cancel'),
      retry: t('crm.run_center.action.retry'),
      cancelSelected: (count: number) =>
        t('crm.run_center.action.cancel_selected', { count: String(count) }),
      retrySelected: (count: number) =>
        t('crm.run_center.action.retry_selected', { count: String(count) }),
    },
    confirm: {
      cancelTitle: t('crm.run_center.confirm.cancel_title'),
      cancelDescription: t('crm.run_center.confirm.cancel_description'),
      retryTitle: t('crm.run_center.confirm.retry_title'),
      retryDescription: t('crm.run_center.confirm.retry_description'),
    },
    bulk: {
      mixedCancelTooltip: t('crm.run_center.bulk.mixed_cancel_tooltip'),
      mixedRetryTooltip: t('crm.run_center.bulk.mixed_retry_tooltip'),
      cancelConfirmTitle: t('crm.run_center.bulk.cancel_confirm_title'),
      cancelConfirmDescription: (count: number) =>
        t('crm.run_center.bulk.cancel_confirm_description', { count: String(count) }),
      retryConfirmTitle: t('crm.run_center.bulk.retry_confirm_title'),
      retryConfirmDescription: (count: number) =>
        t('crm.run_center.bulk.retry_confirm_description', { count: String(count) }),
    },
    success: {
      cancelled: t('crm.run_center.success.cancelled'),
      retried: t('crm.run_center.success.retried'),
    },
    error: {
      cancelFailed: t('crm.run_center.error.cancel_failed'),
      retryFailed: t('crm.run_center.error.retry_failed'),
    },
    statusLabel: (status: RunStatus) => t(STATUS_KEYS[status]),
    outcomeLabel: (outcome: RunOutcome) => t(OUTCOME_KEYS[outcome]),
    approvalLink: t('crm.run_center.approval_link'),
    emptyTitle: t('crm.run_center.empty.title'),
    emptyDescription: t('crm.run_center.empty.description'),
    errorTitle: t('crm.run_center.error.title'),
    errorDescription: t('crm.run_center.error.description'),
    notStarted: t('crm.run_center.not_started'),
    presetFollowUpName: t('crm.run_center.preset.follow_up.name'),
    presetGraphName: t('crm.run_center.preset.graph_follow_up.name'),
    presetMyDayName: t('crm.run_center.preset.my_day.name'),
    scheduledOccurrence: (occurrence: { localDate: string; localTime: string; timezone: string }) =>
      t('crm.run_center.scheduled_occurrence', {
        date: occurrence.localDate,
        time: occurrence.localTime,
        timezone: occurrence.timezone,
      }),
    myDaySummary: (summary: { meetings: string; overdue: string; risks: string; leads: string }) =>
      t('crm.run_center.my_day.summary', summary),
    myDaySummaryTruncated: (summary: string) =>
      t('crm.run_center.my_day.summary_truncated', { summary }),
    commonCancel: t('crm.common.cancel'),
  };
}

const FOLLOW_UP_PRESET_KEY = 'follow-up-after-3-days';
const GRAPH_FOLLOW_UP_PRESET_KEY = 'graph-follow-up-after-3-days';
const MY_DAY_PRESET_KEY = 'morning-my-day';

function getScenarioDisplayName(
  scenario: { name?: string; scenarioName?: string; presetKey: string | null },
  presetFollowUpName: string,
  presetGraphName?: string,
  presetMyDayName?: string,
): string {
  if (scenario.presetKey === FOLLOW_UP_PRESET_KEY) {
    return presetFollowUpName;
  }
  if (scenario.presetKey === GRAPH_FOLLOW_UP_PRESET_KEY && presetGraphName) {
    return presetGraphName;
  }
  if (scenario.presetKey === MY_DAY_PRESET_KEY && presetMyDayName) {
    return presetMyDayName;
  }
  return scenario.scenarioName ?? scenario.name ?? '';
}

function RunCenterJournal(): ReactElement {
  const labels = useRunCenterLabels();
  const { t } = useTranslation();
  // Filters live in the URL like on every other list page: reloading or
  // sharing the link keeps the same slice of the journal.
  const [searchParams, setSearchParams] = useSearchParams();
  const scenarioFilter = searchParams.get('scenarioId') ?? 'all';
  const rawStatus = searchParams.get('status');
  const statusFilter: StatusFilter =
    rawStatus !== null && (ALL_STATUSES as readonly string[]).includes(rawStatus)
      ? (rawStatus as StatusFilter)
      : 'all';
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // setParam pattern (no clobbering): the page reset happens IN THE SAME
  // setSearchParams update, because resetPage() built from the render-time
  // snapshot would overwrite this one and drop the filter.
  const setFilterParam = (name: string, value: string): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === '' || value === 'all') next.delete(name);
        else next.set(name, value);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const { data, isLoading, isError, isFetching, refetch } = useRunCenter({
    limit: RUN_CENTER_OVERVIEW_LIMIT,
  });

  const { cancel: cancelMutation, retry: retryMutation } = useRunCenterMutations();

  const runs = data?.runs ?? [];
  const stats = {
    total: runs.length,
    running: runs.filter((run) => run.status === 'queued' || run.status === 'running').length,
    awaiting: runs.filter((run) => run.status === 'awaiting_approval').length,
    completed: runs.filter((run) => run.status === 'completed').length,
    failed: runs.filter((run) => run.status === 'failed').length,
  };

  const filteredRuns = runs.filter((run) => {
    if (statusFilter !== 'all' && run.status !== statusFilter) return false;
    if (scenarioFilter !== 'all' && run.scenarioId !== scenarioFilter) return false;
    return true;
  });

  // One option per unique scenario id; same-label options stay visible and
  // get a disambiguating suffix (raw scenario name, then id prefix).
  const scenarioOptions = useMemo(
    () =>
      buildScenarioFilterOptions(data?.scenarios ?? [], (scenario) =>
        getScenarioDisplayName(
          scenario,
          labels.presetFollowUpName,
          labels.presetGraphName,
          labels.presetMyDayName,
        ),
      ),
    [data?.scenarios, labels.presetFollowUpName, labels.presetGraphName, labels.presetMyDayName],
  );

  const selectedRuns = useMemo(
    () => filteredRuns.filter((run) => selectedRowKeys.includes(run.id)),
    [filteredRuns, selectedRowKeys],
  );

  const canCancelSelected =
    selectedRuns.length > 0 && selectedRuns.every((run) => run.status === 'awaiting_approval');
  const canRetrySelected =
    selectedRuns.length > 0 && selectedRuns.every((run) => run.status === 'failed');

  const isMutating = cancelMutation.isPending || retryMutation.isPending;

  async function handleCancel(id: string): Promise<void> {
    try {
      await cancelMutation.mutateAsync(id);
      message.success(labels.success.cancelled);
      setSelectedRowKeys((prev) => prev.filter((key) => key !== id));
    } catch {
      // hook already shows error toast; keep local fallback
      message.error(labels.error.cancelFailed);
    }
  }

  async function handleRetry(id: string): Promise<void> {
    try {
      await retryMutation.mutateAsync(id);
      message.success(labels.success.retried);
      setSelectedRowKeys((prev) => prev.filter((key) => key !== id));
    } catch {
      message.error(labels.error.retryFailed);
    }
  }

  function handleBulkCancel(): void {
    if (selectedRowKeys.length === 0) return;
    Modal.confirm({
      title: labels.bulk.cancelConfirmTitle,
      content: labels.bulk.cancelConfirmDescription(selectedRowKeys.length),
      okText: labels.action.cancel,
      okButtonProps: { danger: true },
      cancelText: labels.commonCancel,
      onOk: async () => {
        const ids = [...selectedRowKeys];
        let succeeded = 0;
        let failed = 0;
        for (const id of ids) {
          try {
            await cancelMutation.mutateAsync(id);
            succeeded += 1;
          } catch {
            failed += 1;
          }
        }
        if (succeeded > 0) {
          message.success(labels.success.cancelled);
        }
        if (failed > 0) {
          message.error(labels.error.cancelFailed);
        }
        setSelectedRowKeys([]);
      },
    });
  }

  function handleBulkRetry(): void {
    if (selectedRowKeys.length === 0) return;
    Modal.confirm({
      title: labels.bulk.retryConfirmTitle,
      content: labels.bulk.retryConfirmDescription(selectedRowKeys.length),
      okText: labels.action.retry,
      cancelText: labels.commonCancel,
      onOk: async () => {
        const ids = [...selectedRowKeys];
        let succeeded = 0;
        let failed = 0;
        for (const id of ids) {
          try {
            await retryMutation.mutateAsync(id);
            succeeded += 1;
          } catch {
            failed += 1;
          }
        }
        if (succeeded > 0) {
          message.success(labels.success.retried);
        }
        if (failed > 0) {
          message.error(labels.error.retryFailed);
        }
        setSelectedRowKeys([]);
      },
    });
  }

  // Column plan (incl. per-breakpoint `responsive`) comes from the pure
  // RUN_CENTER_COLUMN_META spec: at xs only scenario/status/approval remain,
  // identity+timing appear from md, operational details from lg.
  // Actions column is always visible (no responsive) with width 120.
  const responsiveByKey = new Map(
    RUN_CENTER_COLUMN_META.map((spec) => [spec.key, spec.responsive]),
  );
  const resp = (key: RunColumnKey): { responsive?: Breakpoint[] } => {
    const value = responsiveByKey.get(key);
    return value === undefined ? {} : { responsive: [...value] };
  };
  const columns: ColumnsType<RunCenterRun> = [
    {
      title: labels.columns.scenario,
      dataIndex: 'scenarioName',
      key: 'scenario',
      render: (_value, run) => (
        <Text strong>
          {getScenarioDisplayName(
            run,
            labels.presetFollowUpName,
            labels.presetGraphName,
            labels.presetMyDayName,
          )}
        </Text>
      ),
    },
    {
      title: labels.columns.deal,
      dataIndex: ['deal', 'title'],
      key: 'deal',
      ...resp('deal'),
      render: (_value, run) =>
        run.deal === null ? (
          <Text type="secondary">—</Text>
        ) : (
          <EntityLink kind="deal" id={run.deal.id} label={run.deal.title} withKindPrefix={false} />
        ),
    },
    {
      title: labels.columns.contact,
      dataIndex: ['contact', 'name'],
      key: 'contact',
      ...resp('contact'),
      render: (_value, run) =>
        run.contact === null ? (
          <Text type="secondary">—</Text>
        ) : (
          <EntityLink
            kind="contact"
            id={run.contact.id}
            label={run.contact.name}
            withKindPrefix={false}
          />
        ),
    },
    {
      title: labels.columns.status,
      dataIndex: 'status',
      key: 'status',
      render: (_value, run) => (
        <EntityStatusChip tone={STATUS_TAG_TONE[run.status]}>
          {labels.statusLabel(run.status)}
        </EntityStatusChip>
      ),
    },
    {
      title: labels.columns.started,
      dataIndex: 'startedAt',
      key: 'started',
      ...resp('started'),
      render: (_value, run) => {
        const started = runStartedDisplay(run);
        return started.kind === 'not_started' ? (
          <Text type="secondary">{labels.notStarted}</Text>
        ) : (
          formatDateTime(started.iso)
        );
      },
    },
    {
      title: labels.columns.finished,
      dataIndex: 'completedAt',
      key: 'finished',
      ...resp('finished'),
      render: (value: string | null) => formatDateTime(value),
    },
    {
      title: labels.columns.scheduled,
      dataIndex: 'nextAttemptAt',
      key: 'scheduled',
      ...resp('scheduled'),
      render: (value: string | null, run: RunCenterRun) => {
        // Scheduled runs show their occurrence slot instead of the retry
        // clock: no fake deal/contact, no raw checkpoint — just the local
        // date, time and explicit timezone from the durable occurrence key.
        const occurrence = run.scheduledOccurrence ?? null;
        if (occurrence !== null) {
          return (
            <span data-testid={`run-scheduled-${run.id}`}>
              {labels.scheduledOccurrence(occurrence)}
            </span>
          );
        }
        return value === null ? (
          <Text type="secondary" data-testid={`run-scheduled-${run.id}`}>
            —
          </Text>
        ) : (
          <span data-testid={`run-scheduled-${run.id}`}>{formatDateTime(value)}</span>
        );
      },
    },
    {
      title: labels.columns.attempts,
      dataIndex: 'attempts',
      key: 'attempts',
      align: 'right' as const,
      ...resp('attempts'),
    },
    {
      title: labels.columns.outcome,
      dataIndex: 'outcome',
      key: 'outcome',
      ...resp('outcome'),
      render: (_value, run) => {
        // Finished briefing runs render their bounded counts instead of the
        // generic bucket: a short localized result, never raw tool outputs.
        const summary = run.summary ?? null;
        if (summary !== null && summary.kind === 'my_day') {
          const line = labels.myDaySummary({
            meetings: String(summary.todayMeetings),
            overdue: String(summary.overdueTasks),
            risks: String(summary.riskDeals),
            leads: String(summary.hotLeads),
          });
          return (
            <Text type="secondary" data-testid={`run-summary-${run.id}`}>
              {summary.truncated ? labels.myDaySummaryTruncated(line) : line}
            </Text>
          );
        }
        return <Text type="secondary">{labels.outcomeLabel(run.outcome)}</Text>;
      },
    },
    {
      // Was an empty header — now a titled column, consistent with the rest
      // of the list standard (the cell is still an icon-style link chip).
      title: labels.columns.approval,
      key: 'approval',
      width: 160,
      render: (_value, run) =>
        run.pendingApprovalId === null ? null : (
          <Link to="/approvals" aria-label={labels.approvalLink}>
            <EntityStatusChip tone="warning" style={{ cursor: 'pointer' }}>
              {labels.approvalLink}
            </EntityStatusChip>
          </Link>
        ),
    },
    {
      title: labels.columns.actions,
      key: 'actions',
      width: 120,
      render: (_value, run) => {
        if (run.status === 'awaiting_approval') {
          const loading = cancelMutation.isPending && cancelMutation.variables === run.id;
          return (
            <Popconfirm
              title={labels.confirm.cancelTitle}
              description={labels.confirm.cancelDescription}
              onConfirm={() => void handleCancel(run.id)}
              okText={labels.action.cancel}
              cancelText={labels.commonCancel}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                size="small"
                loading={loading}
                disabled={isMutating}
                aria-label={`${labels.action.cancel} ${run.id}`}
              >
                {labels.action.cancel}
              </Button>
            </Popconfirm>
          );
        }
        if (run.status === 'failed') {
          const loading = retryMutation.isPending && retryMutation.variables === run.id;
          return (
            <Popconfirm
              title={labels.confirm.retryTitle}
              description={labels.confirm.retryDescription}
              onConfirm={() => void handleRetry(run.id)}
              okText={labels.action.retry}
              cancelText={labels.commonCancel}
            >
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={loading}
                disabled={isMutating}
                aria-label={`${labels.action.retry} ${run.id}`}
              >
                {labels.action.retry}
              </Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  return (
    <main
      className="animate-fade-in flex flex-col mx-auto w-full overflow-x-hidden p-4 md:p-6"
      style={{ gap: 16 }}
    >
      <PageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        actions={
          <Tooltip title={labels.refresh}>
            <Button
              aria-label={labels.refresh}
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => void refetch()}
              style={LIST_ICON_BUTTON_STYLE}
            />
          </Tooltip>
        }
      />

      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} lg={4}>
          <Card size="small">
            <Statistic title={labels.stats.total} value={stats.total} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={5}>
          <Card size="small">
            <Statistic title={labels.stats.running} value={stats.running} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={5}>
          <Card size="small">
            <Statistic title={labels.stats.awaiting} value={stats.awaiting} />
          </Card>
        </Col>
        <Col xs={12} md={12} lg={5}>
          <Card size="small">
            <Statistic title={labels.stats.completed} value={stats.completed} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={5}>
          <Card size="small">
            <Statistic title={labels.stats.failed} value={stats.failed} />
          </Card>
        </Col>
      </Row>

      <FilterBar>
        <FilterSelect
          aria-label={labels.scenarioFilter}
          value={scenarioFilter}
          onChange={(value) => {
            setFilterParam('scenarioId', value);
            setSelectedRowKeys([]);
          }}
          options={[{ value: 'all', label: labels.allScenarios }, ...scenarioOptions]}
        />
        <FilterSelect
          aria-label={labels.statusFilter}
          value={statusFilter}
          onChange={(value) => {
            setFilterParam('status', value);
            setSelectedRowKeys([]);
          }}
          options={[
            { value: 'all', label: labels.allStatuses },
            ...ALL_STATUSES.map((status) => ({
              value: status,
              label: labels.statusLabel(status),
            })),
          ]}
        />
      </FilterBar>

      {selectedRowKeys.length > 0 ? (
        <Card size="small" data-testid="run-center-bulk-bar">
          <Space wrap>
            <Text type="secondary">
              {t('crm.approvals.bulk.count', { count: String(selectedRowKeys.length) })}
            </Text>
            <Tooltip
              title={
                !canCancelSelected && selectedRuns.length > 0
                  ? labels.bulk.mixedCancelTooltip
                  : undefined
              }
            >
              <Button
                danger
                size="small"
                disabled={!canCancelSelected || isMutating}
                onClick={handleBulkCancel}
                aria-label={labels.action.cancelSelected(selectedRowKeys.length)}
              >
                {labels.action.cancelSelected(selectedRowKeys.length)}
              </Button>
            </Tooltip>
            <Tooltip
              title={
                !canRetrySelected && selectedRuns.length > 0
                  ? labels.bulk.mixedRetryTooltip
                  : undefined
              }
            >
              <Button
                size="small"
                icon={<ReloadOutlined />}
                disabled={!canRetrySelected || isMutating}
                onClick={handleBulkRetry}
                aria-label={labels.action.retrySelected(selectedRowKeys.length)}
              >
                {labels.action.retrySelected(selectedRowKeys.length)}
              </Button>
            </Tooltip>
            <Button size="small" type="text" onClick={() => setSelectedRowKeys([])}>
              {labels.commonCancel}
            </Button>
          </Space>
        </Card>
      ) : null}

      {isError ? (
        <ErrorState
          title={labels.errorTitle}
          fallbackDescription={labels.errorDescription}
          onRetry={() => void refetch()}
        />
      ) : isLoading ? (
        <div data-testid="run-center-loading" style={LIST_SURFACE_STYLE}>
          <QuietListSkeleton rows={6} />
        </div>
      ) : filteredRuns.length === 0 ? (
        <div style={LIST_SURFACE_STYLE}>
          {/* Тихое empty (spec D): без иллюстраций — заголовок и строка-подпись. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: '20px 16px',
            }}
          >
            <Text strong>{labels.emptyTitle}</Text>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
              {labels.emptyDescription}
            </span>
          </div>
        </div>
      ) : (
        <div
          className="crm-checkbox-square"
          data-testid="run-center-table-container"
          style={LIST_SURFACE_STYLE}
        >
          <EntityTable<RunCenterRun>
            data-testid="run-center-table"
            dataSource={filteredRuns}
            columns={columns}
            // No explicit pagination config: EntityTable owns URL-synced
            // pagination (?page/?pageSize), same as every other list page.
            loading={isFetching && runs.length === 0}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys.map(String)),
              preserveSelectedRowKeys: false,
              getCheckboxProps: () => ({ disabled: isMutating }),
            }}
          />
        </div>
      )}
    </main>
  );
}

/**
 * Paywall screen for the Run Center (ADR-029). Rendered instead of the journal
 * only when the backend shipped a billing payload AND `run-center:view` was
 * explicitly denied — otherwise the page fails open and never blocks readers.
 */
function RunCenterUpgradePrompt(): ReactElement {
  const { t } = useTranslation();
  return (
    <main
      className="animate-fade-in flex flex-col mx-auto w-full overflow-x-hidden p-4 md:p-6"
      style={{ gap: 16 }}
    >
      <Card size="small">
        <Result
          status="warning"
          title={t('crm.run_center.upgrade.title')}
          subTitle={t('crm.run_center.upgrade.description')}
          extra={
            <Button type="primary" href="/settings">
              {t('crm.run_center.upgrade.action')}
            </Button>
          }
        />
      </Card>
    </main>
  );
}

export default function RunCenterPage(): ReactElement {
  // Fail-open: `useBillingAccess` reports `allowed: true` when no billing
  // payload was shipped, so pre-billing servers keep rendering the journal.
  const billing = useBillingAccess();
  const runCenterDenied = billing.runCenter.allowed === false;

  if (runCenterDenied) {
    return <RunCenterUpgradePrompt />;
  }
  return <RunCenterJournal />;
}
